// Base Implementation copied over from https://github.com/adobe/aem-react-core-wcm-components-spa/tree/master/src/container/tabs/v1

import React from 'react';
import {
  CoreContainerState,
  TabsV1IsEmptyFn,
  TabsV1Properties,
  withAuthorPanelSwitch,
  withStandardBaseCssClass,
} from '@adobe/aem-core-components-react-spa';
import {
  ComponentMapping,
  Container,
} from '@adobe/aem-react-editable-components';
import styled, { css, keyframes } from 'styled-components';
import {
  TabOption,
  TabOptionWithIconUrl,
  Tabs,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { TabsProps } from './TabsEditConfig';

export interface TabsV1State extends CoreContainerState {
  activeIndex: number;
}

const animateOpacity = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1.0);
  }
`;

interface TabDetailsContaineProps extends React.HTMLProps<HTMLDivElement> {
  isOpen: boolean;
}

const TabDetailsContainer = styled.div<TabDetailsContaineProps>`
  ${({ isOpen }) => css`
    display: ${isOpen ? 'block' : 'none'};
    animation: ${animateOpacity} 0.4s;
  `}
`;

class TabsV1Impl extends Container<TabsProps, TabsV1State> {
  constructor(props: TabsProps) {
    super(props);

    this.state = {
      activeIndex:
        !!props.activeItem && props.activeItem.length > 0
          ? this.props.cqItemsOrder.indexOf(props.activeItem)
          : 0,
      componentMapping: this.props.componentMapping || ComponentMapping,
    };

    this.handleTabNavClick = this.handleTabNavClick.bind(this);
  }

  componentDidUpdate(prevProps: Readonly<TabsV1Properties>): void {
    if (
      this.props.activeIndexFromAuthorPanel !== undefined &&
      prevProps.activeIndexFromAuthorPanel !==
        this.props.activeIndexFromAuthorPanel
    ) {
      this.setState({ activeIndex: this.props.activeIndexFromAuthorPanel });
    }
  }

  tabbedChildComponents() {
    if (this.props.isInEditor === true) {
      //for editing capabilities to work properly, we always need to render each item.
      //we will hide the disabled items instead.
      return (
        <div>
          {this.childComponents.map((_item, index) => {
            const isOpen = this.state.activeIndex === index;
            return (
              <TabDetailsContainer
                key={'tab-content-' + index}
                className={this.props.baseCssClass + '__author-tab-content'}
                isOpen={isOpen}
              >
                {this.childComponents[index]}
              </TabDetailsContainer>
            );
          })}
        </div>
      );
    } else {
      //when the editor is disabled, we can just show the active item only.
      const options: TabOption<string>[] = this.props.cqItemsOrder.map(
        (item, _index) => {
          const tab: { 'cq:panelTitle': string } = this.props.cqItems[item];
          return {
            value: item,
            label: tab['cq:panelTitle'],
          };
        },
      );

      return (
        <LinkRegion
          regionId={`${this.props.regionId || 'tabs'}.${
            options[this.state.activeIndex].label
          }`}
        >
          {this.childComponents[this.state.activeIndex]}
        </LinkRegion>
      );
    }
  }

  handleTabNavClick(index: number) {
    if (this.state.activeIndex !== index) {
      this.setState({
        activeIndex: index,
      });
    }
  }

  tabNavigation(options: TabOption<string>[]) {
    const childComponents: JSX.Element[] = [];

    if (!this.props.cqItems || !this.props.cqItemsOrder) {
      return childComponents;
    }

    // Add these props to tabs components
    //this.props.accessibilityLabel
    //aria-multiselectable="false"
    return (
      <LinkRegion regionId={this.props.regionId || 'tabs'}>
        <Tabs
          onTabSelected={(selected: string) => {
            trackCustomLink(selected);
            this.setState({
              activeIndex: options.findIndex((item) => item.value === selected),
            });
          }}
          options={options}
          selected={options[this.state.activeIndex].value}
        />
      </LinkRegion>
    );
  }

  get tabContainerProps() {
    const attrs = this.containerProps;
    attrs['className'] = attrs.className + ' ' + this.props.baseCssClass;
    attrs['data-cmp-is'] = 'tabs';
    attrs['data-panelcontainer'] = 'tabs';
    return attrs;
  }

  render() {
    const tabIcons = this.props.componentProperties?.tabIcons;
    const isEmpty = TabsV1IsEmptyFn(this.props);
    const options: TabOptionWithIconUrl<string>[] = this.props.cqItemsOrder.map(
      (item, index) => {
        const tab = this.props.cqItems[item];
        // Add an additional property inside tabs to be able to use images as well.

        return {
          value: item,
          label: tab['cq:panelTitle'],
          iconUrl: tabIcons?.[index]?.tabIconPath || '',
        };
      },
    );

    return (
      <div {...this.tabContainerProps}>
        {!isEmpty && this.tabNavigation(options)}
        {!isEmpty && this.tabbedChildComponents()}
        {this.placeholderComponent}
      </div>
    );
  }
}

export default withStandardBaseCssClass(
  withAuthorPanelSwitch(TabsV1Impl),
  'cmp-tabs',
);
